/*
 * <BeScrollView> -- React wrapper for the .be-scroll-view composition frame:
 * a content pane with real BeOS scrollbars (see ScrollBar.tsx /
 * components/scrollbar.css), the optional Tracker-style bottom status cells,
 * and the corner gap a document window leaves for the resize knob.
 *
 * The content is a native scroller with the UA bars hidden, so wheel/touch/
 * keyboard scrolling all work; the Be bars mirror scrollLeft/Top both ways.
 * Ranges follow BScrollBar convention: value = scroll offset in px,
 * proportion = visible fraction. Content that fits renders the disabled flat
 * track, like the server does for an empty range.
 *
 *   <BeScrollView horizontal vertical corner status={<BeCountView count={12} />}>
 *     <BeTable ... />
 *   </BeScrollView>
 */

import { type CSSProperties, type ReactNode, type UIEvent, useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { BeScrollBar, type BeScrollKnob } from "./ScrollBar.tsx";

export type BeScrollViewBorder = "none" | "plain" | "fancy";

export type BeScrollViewProps = BeProps<
	"div",
	{
		/** Which bars to render (BScrollView's h_scroll/v_scroll ctor args). */
		horizontal?: boolean;
		vertical?: boolean;
		/**
		 * Drop a bar whose range is empty, instead of drawing the disabled flat
		 * track R5 leaves there. Opt-in: the track is what the server does, so it
		 * stays the default.
		 *
		 * Removing a bar gives the content its width back, so anything that
		 * reflows can change whether it overflows at all. Safe for prose in a
		 * fixed-width pane; think twice on the horizontal axis.
		 */
		autoHide?: boolean;
		/** B_FANCY_BORDER is the BScrollView default; Tracker mounts with none. */
		border?: BeScrollViewBorder;
		/** Content surface: "list" = white pose view (default), "panel" = chrome grey. */
		surface?: "list" | "panel";
		/** Leading cells for the bottom strip (e.g. <BeCountView>). */
		status?: ReactNode;
		/** Reserve the bottom-right square. On when both bars show; a resizable window needs it either way, or its grab zone covers the down arrow. */
		corner?: boolean;
		/** Grip style, passed to both bars. See {@link BeScrollKnob}. */
		knob?: BeScrollKnob;
		/** Dim both grips, as an unfocused window does. */
		inactive?: boolean;
		/**
		 * Take the box this is mounted in instead of sizing to content, so the
		 * content scrolls rather than the ancestor. Needs a parent with a bounded
		 * height -- <BeWindow fill> / <BeTabView fill> supply one.
		 */
		fill?: boolean;
		/** Inline style merged onto the scrolling content pane. */
		contentStyle?: CSSProperties;
		children?: ReactNode;
	}
>;

export const BeScrollView = beComponent<BeScrollViewProps, HTMLDivElement>("BeScrollView", (props, be) => {
	const {
		horizontal,
		vertical = true,
		autoHide,
		border = "fancy",
		surface = "list",
		status,
		corner,
		knob,
		inactive,
		scale,
		fill,
		className,
		style,
		contentStyle,
		children,
		...rest
	} = props;

	const contentRef = useRef<HTMLDivElement>(null);
	const contentId = useId();
	const [scroll, setScroll] = useState({ x: 0, y: 0, maxX: 0, maxY: 0, propX: 1, propY: 1, clientW: 0, clientH: 0 });

	const measure = useCallback(() => {
		const el = contentRef.current;
		if (!el) return;
		setScroll({
			x: el.scrollLeft,
			y: el.scrollTop,
			maxX: Math.max(0, el.scrollWidth - el.clientWidth),
			maxY: Math.max(0, el.scrollHeight - el.clientHeight),
			propX: el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1,
			propY: el.scrollHeight > 0 ? el.clientHeight / el.scrollHeight : 1,
			clientW: el.clientWidth,
			clientH: el.clientHeight,
		});
	}, []);

	useLayoutEffect(() => {
		const el = contentRef.current;
		if (!el) return;
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		for (const child of el.children) ro.observe(child);
		return () => ro.disconnect();
	}, [measure]);

	const onScroll = (_e: UIEvent<HTMLDivElement>) => measure();

	const scrollTo = (axis: "x" | "y") => (v: number) => {
		const el = contentRef.current;
		if (!el) return;
		if (axis === "x") el.scrollLeft = v;
		else el.scrollTop = v;
		measure();
	};

	const showV = vertical && !(autoHide && scroll.maxY === 0);
	const showH = horizontal && !(autoHide && scroll.maxX === 0);
	const showCorner = corner ?? (showH && showV);

	const root = be(
		"be-scroll-view",
		border === "fancy" && "be-scroll-view--fancy",
		border === "plain" && "be-scroll-view--plain",
		surface === "panel" && "be-scroll-view--panel",
		fill && "is-fill",
	);

	// BeOS line/page steps, in px: a line is ~1 row, a page most of a view.
	const barProps = { knob, inactive, smallStep: 12, controls: contentId };

	return (
		<div {...rest} {...root}>
			<div id={contentId} className="be-scroll-view__content" style={contentStyle} ref={contentRef} onScroll={onScroll}>
				{children}
			</div>
			{showV && (
				<BeScrollBar
					{...barProps}
					orientation="vertical"
					value={scroll.y}
					max={scroll.maxY}
					proportion={scroll.propY}
					bigStep={Math.max(24, scroll.clientH * 0.9)}
					onChange={scrollTo("y")}
					label="Vertical scroll bar"
				/>
			)}
			{(showH || status !== undefined) && (
				<div className="be-scroll-view__bottom">
					{status}
					{showH && (
						<BeScrollBar
							{...barProps}
							orientation="horizontal"
							value={scroll.x}
							max={scroll.maxX}
							proportion={scroll.propX}
							bigStep={Math.max(24, scroll.clientW * 0.9)}
							onChange={scrollTo("x")}
							label="Horizontal scroll bar"
						/>
					)}
				</div>
			)}
			{showCorner && <div className="be-scroll-view__corner" />}
		</div>
	);
});
